import React, { useState, useCallback } from 'react';
import { FaTasks, FaLayerGroup, FaCheck, FaCommentDots, FaChevronDown, FaChevronUp } from 'react-icons/fa';

/**
 * ActionItemTasksTab
 * Renders the task checklists defined in the action item's template.
 * Users can check off items and add comments per item.
 *
 * @param {Array} taskGroups - Task groups from the template schema's `tasks` array
 * @param {object} taskValues - Current task completion/comment state keyed by `groupIdx_itemIdx`
 * @param {function} onTaskChange - Callback (key, { checked, comments }) when a task value changes
 * @param {boolean} readOnly - Whether the action item is closed/read-only
 */
const ActionItemTasksTab = ({ taskGroups = [], taskValues = {}, onTaskChange, readOnly = false }) => {
    const [expandedGroups, setExpandedGroups] = useState(() =>
        Object.fromEntries(taskGroups.map((_, i) => [i, true]))
    );
    const [commentOpen, setCommentOpen] = useState({});

    const toggleGroup = (idx) => {
        setExpandedGroups(prev => ({ ...prev, [idx]: !prev[idx] }));
    };

    const toggleComment = (key) => {
        setCommentOpen(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const getVal = useCallback((key) => taskValues[key] || { checked: false, comments: '' }, [taskValues]);

    if (!taskGroups || taskGroups.length === 0) {
        return (
            <div className="activity-tab-panel" style={{ textAlign: 'center', padding: '40px 20px', color: '#a0aec0' }}>
                <FaTasks style={{ fontSize: '2rem', marginBottom: '12px', opacity: 0.4 }} />
                <p style={{ margin: 0, fontWeight: 600, color: '#718096' }}>No tasks defined</p>
                <p style={{ margin: '4px 0 0', fontSize: '0.82rem' }}>
                    This template does not have any task checklists configured.
                </p>
            </div>
        );
    }

    const totalItems = taskGroups.reduce((sum, g) => sum + (g.items?.length || 0), 0);
    const completedItems = Object.values(taskValues).filter(v => v.checked).length;

    return (
        <div className="activity-tab-panel action-item-tasks-tab">
            {/* Progress summary */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', padding: '10px 14px', background: '#f7fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <FaTasks style={{ color: '#8b6e5a' }} />
                <span style={{ fontSize: '0.85rem', color: '#4a5568', fontWeight: 600 }}>
                    {completedItems} / {totalItems} completed
                </span>
                <div style={{ flex: 1, height: '6px', background: '#e2e8f0', borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{
                        width: totalItems > 0 ? `${(completedItems / totalItems) * 100}%` : '0%',
                        height: '100%',
                        background: completedItems === totalItems && totalItems > 0 ? '#38a169' : '#F63040',
                        borderRadius: '3px',
                        transition: 'width 0.3s ease'
                    }} />
                </div>
            </div>

            {taskGroups.map((group, gIdx) => (
                <div key={gIdx} style={{ border: '1px solid #e2e8f0', borderRadius: '8px', marginBottom: '12px', background: '#fff' }}>
                    <button
                        type="button"
                        onClick={() => toggleGroup(gIdx)}
                        style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', padding: '10px 14px', background: '#f7fafc', border: 'none', borderRadius: expandedGroups[gIdx] ? '8px 8px 0 0' : '8px', cursor: 'pointer', textAlign: 'left', borderBottom: expandedGroups[gIdx] ? '1px solid #e2e8f0' : 'none' }}
                    >
                        <FaLayerGroup style={{ color: '#8b6e5a' }} />
                        <span style={{ flex: 1, fontWeight: 600, fontSize: '0.9rem', color: '#2d3748' }}>{group.name}</span>
                        <span style={{ fontSize: '0.78rem', color: '#a0aec0' }}>
                            {(group.items || []).filter((_, iIdx) => getVal(`${gIdx}_${iIdx}`).checked).length}/{(group.items || []).length}
                        </span>
                        {expandedGroups[gIdx] ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                    </button>

                    {expandedGroups[gIdx] && (
                        <div style={{ padding: '8px 14px' }}>
                            {(group.items || []).map((item, iIdx) => {
                                const key = `${gIdx}_${iIdx}`;
                                const val = getVal(key);
                                return (
                                    <div key={iIdx} style={{ display: 'flex', flexDirection: 'column', padding: '8px 0', borderBottom: iIdx < group.items.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <label style={{ display: 'flex', alignItems: 'center', cursor: readOnly ? 'default' : 'pointer' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={!!val.checked}
                                                    onChange={(e) => onTaskChange(key, { ...val, checked: e.target.checked })}
                                                    disabled={readOnly}
                                                    style={{ width: '18px', height: '18px', accentColor: '#38a169', cursor: readOnly ? 'default' : 'pointer' }}
                                                />
                                            </label>
                                            <div style={{ flex: 1 }}>
                                                <span style={{ fontSize: '0.88rem', color: val.checked ? '#a0aec0' : '#2d3748', textDecoration: val.checked ? 'line-through' : 'none', fontWeight: 500 }}>
                                                    {item.label}
                                                </span>
                                                {item.description && (
                                                    <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: '#a0aec0' }}>{item.description}</p>
                                                )}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => toggleComment(key)}
                                                title="Add comment"
                                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: val.comments ? '#F63040' : '#cbd5e0', padding: '4px' }}
                                            >
                                                <FaCommentDots size={14} />
                                            </button>
                                        </div>
                                        {commentOpen[key] && (
                                            <textarea
                                                value={val.comments || ''}
                                                onChange={(e) => onTaskChange(key, { ...val, comments: e.target.value })}
                                                disabled={readOnly}
                                                placeholder="Add a comment..."
                                                rows={2}
                                                style={{ marginTop: '6px', marginLeft: '28px', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '6px 10px', fontSize: '0.82rem', resize: 'vertical', width: 'calc(100% - 28px)' }}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

export default ActionItemTasksTab;